import React, { useState, useMemo } from "react";
import { Card, Table, Tag, Button } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// TypeScript Interfaces
interface ChannelData {
  metric: number;
  response: number;
  channel: string;
  channelroi: number;
}

interface ProcessedChannelData extends ChannelData {
  efficiency: number;
  roiCategory: "excellent" | "good" | "average" | "poor";
  spendFormatted: string;
  responseFormatted: string;
}

interface Props {
  rawData: ChannelData[];
  spendShare: any;
}

const MediaChannelDashboardRoi: React.FC<Props> = ({ rawData, spendShare }) => {
  const [sortBy, setSortBy] = useState<string>("channelroi");
  const [viewMode, setViewMode] = useState<"table" | "charts">("table");
  // Process data
  const processedData: ProcessedChannelData[] = useMemo(() => {
    return rawData.map((item) => {
      const efficiency =
        item.metric > 0 ? (item.response / item.metric) * 100 : 0;
console.log(rawData,"rawData")
      let roiCategory: "excellent" | "good" | "average" | "poor";
      if (item.channelroi > 2) roiCategory = "excellent";
      else if (item.channelroi > 0.5) roiCategory = "good";
      else if (item.channelroi > 0.1) roiCategory = "average";
      else roiCategory = "poor";

      // Map response label from Contributions_chart
      const contribution = spendShare?.Contributions_chart?.find(
        (entry: any) => entry.Variables === item.channel
      );

      const responseLabel = contribution
        ? `${contribution.Labels.replace(")", "%)")}`
        : item.response.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          });

      // Map spend from spendShare
      const spend = spendShare?.spendShare?.[item.channel];
      const spendFormatted =
        typeof spend === "number" ? `${spend.toFixed(2)}%` : "0.00%";

      return {
        ...item,
        efficiency,
        roiCategory,
        spendFormatted,
        responseFormatted: responseLabel,
      };
    });
  }, [rawData, spendShare]);
console.log(processedData,"processedData")
  // Sort data
  const sortedData = useMemo(() => {
    return [...processedData].sort((a, b) => {
      switch (sortBy) {
        case "channelroi":
          return b.channelroi - a.channelroi;
        case "metric":
          return b.metric - a.metric;
        case "response":
          return b.response - a.response;
        case "efficiency":
          return b.efficiency - a.efficiency;
        default:
          return 0;
      }
    });
  }, [processedData, sortBy]);

  // ROI category colors and labels
  const getRoiTagProps = (category: string) => {
    switch (category) {
      case "excellent":
        return { color: "green", label: "Excellent" };
      case "good":
        return { color: "blue", label: "Good" };
      case "average":
        return { color: "orange", label: "Average" };
      case "poor":
        return { color: "red", label: "Poor" };
      default:
        return { color: "default", label: "Unknown" };
    }
  };
  // Table columns
  const tableColumns = [
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (text: string) => (
        <span className="font-medium text-gray-800">
          {text.replace("_", " ").replace("Spend", "")}
        </span>
      ),
    },
    {
      title: "Spend",
      dataIndex: "spendFormatted",
      key: "spend",
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.metric - b.metric,
    },
    {
      title: "Effect",
      dataIndex: "responseFormatted",
      key: "response",
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.response - b.response,
    },
    {
      title: "ROI",
      dataIndex: "channelroi",
      key: "roi",
      render: (value: number) => (
        <span
          className={`font-semibold ${
            value > 1
              ? "text-green-600"
              : value > 0.5
              ? "text-blue-600"
              : "text-red-600"
          }`}
        >
          {(value * 100).toFixed(2)}%
        </span>
      ),
      sorter: (a: ProcessedChannelData, b: ProcessedChannelData) =>
        a.channelroi - b.channelroi,
    },
    {
      title: "Performance",
      dataIndex: "roiCategory",
      key: "performance",
      render: (category: string) => {
        const props = getRoiTagProps(category);
        return <Tag color={props.color}>{props.label}</Tag>;
      },
    },
  ];

  return (
    <div className="">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Marketing Channel ROI Analysis
          </h1>
          <p className="text-gray-600">
            Compare performance across different marketing channels
          </p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                onClick={() => setViewMode("table")}
              >
                Table
              </Button>
              <Button
                type={viewMode === "charts" ? "primary" : "default"}
                onClick={() => setViewMode("charts")}
              >
                Charts
              </Button>
            </div>
          </div>
        </Card>

        {viewMode === "table" && (
          <Card>
            <Table
              dataSource={sortedData}
              columns={tableColumns}
              rowKey="channel"
              pagination={false}
              scroll={{ x: 800 }}
            />
          </Card>
        )}

        {viewMode === "charts" && (
          <div>
            <div>
              <Card title="ROI Comparison by Channel">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={sortedData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="channel"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tickFormatter={(value: any) =>
                        value.replace("_Spend", "").replace("_", " ")
                      }
                    />
                    <YAxis />
                    <Bar dataKey="channelroi" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaChannelDashboardRoi;
